import { all, call, fork, put, take } from "redux-saga/effects";

import { Entry } from "../../domain/model/entry";
import { Customer } from "../../domain/model/customer";
import { Project } from "../../domain/model/project";
import { Contract } from "../../domain/model/contract";
import { IEntryRepository } from "../../domain/repository/entry_repository";
import { IProjectRepository } from "../../domain/repository/project_repository";
import { ICustomerRepository } from "../../domain/repository/customer_repository";
import {
  IContractRepository,
  TContractIdWithoutCompany,
} from "../../domain/repository/contract_repository";

import { createApiClient, IApiClient } from "../../infra/api/client";
import { createEntryAPI } from "../../infra/api/service/entry_api";
import { createProjectAPI } from "../../infra/api/service/project_api";
import { createCustomerAPI } from "../../infra/api/service/customer_api";
import { createContractAPI } from "../../infra/api/service/contract_api";

import {
  ClientEntriesActionType,
  createClientEntriesActionCreator,
  IClientEntriesActionCreator,
  IRequestGetAllEntriesAction,
} from "../action/client_entries_action";

import { GetAllEntriesByClientResponse } from "../response/entry_response";
import { handleErrorForHandler } from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const entryRepository: IEntryRepository = createEntryAPI(apiClient);
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);
const contractRepository: IContractRepository = createContractAPI(apiClient);
const actionCreator: IClientEntriesActionCreator =
  createClientEntriesActionCreator();

function* handleGetAllEntriesInClientEntries() {
  while (true) {
    try {
      const action: IRequestGetAllEntriesAction = yield take(
        ClientEntriesActionType.REQUEST_GET_ALL_ENTRY_LIST
      );
      const entries: Entry[] = yield call(
        getAllByProjectId,
        action.item.projectId
      );
      const customerIdList: string[] = yield call(
        createCustomerIdList,
        entries
      );
      const ids: TContractIdWithoutCompany[] = yield call(
        createContractIdWithoutCompanyIdList,
        action.item.projectId,
        customerIdList
      );
      let project: Project;
      let customers: Customer[];
      let contracts: Contract[];
      [project, customers, contracts] = yield all([
        call(getProject, action.item.projectId),
        call(getAllByCustomerIdList, customerIdList),
        call(getContractAllNewestByIDs, ids),
      ]);
      const res: GetAllEntriesByClientResponse =
        new GetAllEntriesByClientResponse();
      res.entries = entries;
      res.customers = customers;
      res.contracts = contracts;
      res.project = project;
      yield put(actionCreator.callbackGetAllEntriesAction(true, res));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackGetAllEntriesAction(
          false,
          undefined,
          error.toString()
        )
      );
    }
  }
}

const getAllByProjectId = (projectId: string): Promise<Entry[]> => {
  return entryRepository.getAllByProject(projectId);
};

const getProject = (projectId: string): Promise<Project> => {
  return projectRepository.get(projectId);
};

const getContractAllNewestByIDs = (
  ids: TContractIdWithoutCompany[]
): Promise<Contract[]> => {
  return contractRepository.getMulti(ids);
};

const createCustomerIdList = (entryList: Entry[]): string[] => {
  return entryList.map((item: Entry) => {
    return item.customerID;
  });
};

const createContractIdWithoutCompanyIdList = (
  projectId: string,
  customerIdList: string[]
): TContractIdWithoutCompany[] => {
  return customerIdList.map((item: string) => {
    const res: TContractIdWithoutCompany = {
      projectID: projectId,
      customerID: item,
    };
    return res;
  });
};

const getAllByCustomerIdList = (
  customerIdList: string[]
): Promise<Customer[]> => {
  return customerRepository.getAllByIDs(customerIdList);
};

export { handleGetAllEntriesInClientEntries };



// WEBPACK FOOTER //
// ./src/presentation/handler/client_entries_handler.ts